import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from './useAuth';
import { saveCart, loadCart, clearCart as clearPersistedCart } from '@/lib/utils/cartPersistence';
import { variantsEqual, variantDataEqual } from '@/lib/utils/comparison';
import type {
  Cart,
  CartItem,
  RegistrationWizardState,
  Tournament,
  Activity,
  Addon,
  SpecialEvent,
  FlowConfig,
} from '@/types';

const emptyCart: Cart = {
  items: [],
  subtotal: 0,
  tax: 0,
  total: 0,
};

const emptyWizardState: RegistrationWizardState = {
  currentStep: 0,
  completedSteps: [],
  cart: emptyCart,
  eventRegistration: {
    wantsToPay: true,
    entryType: undefined,
    entryFee: undefined,
  },
  tournaments: {
    selectedIds: [],
  },
  addons: {
    selections: [],
  },
};

interface UseCartOptions {
  taxRate?: number;
  onCartChange?: (cart: Cart) => void;
}

export function useCart(options: UseCartOptions = {}) {
  const { user } = useAuth();
  const userId = user?.id;
  const { taxRate = 0, onCartChange } = options;

  const [cart, setCart] = useState<Cart>(emptyCart);
  const [wizardState, setWizardState] = useState<RegistrationWizardState>(emptyWizardState);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load persisted cart on mount
  useEffect(() => {
    // Track if this effect is still active to prevent stale updates
    let isStale = false;

    const stored = loadCart(userId);
    if (!isStale && stored) {
      setCart(stored.cart);
      setWizardState((prev) => ({
        ...prev,
        ...stored.wizardState,
        cart: stored.cart,
      }));
    }
    if (!isStale) {
      setIsHydrated(true);
    }

    return () => {
      isStale = true;
    };
  }, [userId]);

  // Persist cart on change
  useEffect(() => {
    if (isHydrated) {
      saveCart(cart, wizardState, userId);
      onCartChange?.(cart);
    }
  }, [cart, wizardState, userId, isHydrated, onCartChange]);

  // Calculate totals
  const calculateTotals = useCallback(
    (items: CartItem[]): Omit<Cart, 'items'> => {
      const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const tax = Math.round(subtotal * taxRate);
      const total = subtotal + tax;
      return { subtotal, tax, total };
    },
    [taxRate]
  );

  // Add item to cart
  const addItem = useCallback(
    (item: Omit<CartItem, 'id'>) => {
      setCart((prev) => {
        // Check if item already exists (same type, itemId, and variant)
        const existingIndex = prev.items.findIndex(
          (i) =>
            i.type === item.type &&
            i.itemId === item.itemId &&
            variantsEqual(i.variant, item.variant)
        );

        let newItems: CartItem[];
        if (existingIndex >= 0) {
          // Update quantity
          newItems = prev.items.map((i, idx) =>
            idx === existingIndex ? { ...i, quantity: i.quantity + item.quantity } : i
          );
        } else {
          // Add new item
          const newItem: CartItem = {
            ...item,
            id: crypto.randomUUID(),
          };
          newItems = [...prev.items, newItem];
        }

        return { items: newItems, ...calculateTotals(newItems) };
      });
    },
    [calculateTotals]
  );

  // Remove item from cart
  const removeItem = useCallback(
    (itemId: string) => {
      setCart((prev) => {
        const newItems = prev.items.filter((item) => item.id !== itemId);
        return { items: newItems, ...calculateTotals(newItems) };
      });
    },
    [calculateTotals]
  );

  // Update item quantity
  const updateQuantity = useCallback(
    (itemId: string, quantity: number) => {
      if (quantity <= 0) {
        removeItem(itemId);
        return;
      }

      setCart((prev) => {
        const newItems = prev.items.map((item) =>
          item.id === itemId ? { ...item, quantity } : item
        );
        return { items: newItems, ...calculateTotals(newItems) };
      });
    },
    [calculateTotals, removeItem]
  );

  // Clear cart
  const clearCart = useCallback(() => {
    setCart(emptyCart);
    setWizardState(emptyWizardState);
    clearPersistedCart(userId);
  }, [userId]);

  // Helper functions for specific item types
  const addEventRegistration = useCallback(
    (entryType: 'free' | 'spectator' | 'paid', price: number, name: string) => {
      // Remove any existing event registration
      setCart((prev) => {
        const filtered = prev.items.filter((item) => item.type !== 'event_registration');
        const newItem: CartItem = {
          id: crypto.randomUUID(),
          type: 'event_registration',
          itemId: 'event',
          name,
          price,
          quantity: 1,
          variant: { name: entryType, data: { entryType } },
        };
        const newItems = [...filtered, newItem];
        return { items: newItems, ...calculateTotals(newItems) };
      });

      setWizardState((prev) => ({
        ...prev,
        eventRegistration: {
          ...prev.eventRegistration,
          entryType,
          entryFee: price,
        },
      }));
    },
    [calculateTotals]
  );

  const addTournament = useCallback(
    (tournament: Tournament, price?: number) => {
      const effectivePrice = price ?? tournament.registrationFee;
      addItem({
        type: 'tournament',
        itemId: tournament.id,
        name: tournament.name,
        price: effectivePrice,
        quantity: 1,
      });

      setWizardState((prev) => ({
        ...prev,
        tournaments: {
          ...prev.tournaments,
          selectedIds: [...prev.tournaments.selectedIds, tournament.id],
        },
      }));
    },
    [addItem]
  );

  const removeTournament = useCallback(
    (tournamentId: string) => {
      setCart((prev) => {
        const newItems = prev.items.filter(
          (item) => !(item.type === 'tournament' && item.itemId === tournamentId)
        );
        return { items: newItems, ...calculateTotals(newItems) };
      });

      setWizardState((prev) => ({
        ...prev,
        tournaments: {
          ...prev.tournaments,
          selectedIds: prev.tournaments.selectedIds.filter((id) => id !== tournamentId),
        },
      }));
    },
    [calculateTotals]
  );

  const addActivity = useCallback(
    (activity: Activity, price?: number) => {
      const effectivePrice = price ?? activity.fee;
      addItem({
        type: 'activity',
        itemId: activity.id,
        name: activity.title,
        price: effectivePrice,
        quantity: 1,
      });
    },
    [addItem]
  );

  const removeActivity = useCallback(
    (activityId: string) => {
      setCart((prev) => {
        const newItems = prev.items.filter(
          (item) => !(item.type === 'activity' && item.itemId === activityId)
        );
        return { items: newItems, ...calculateTotals(newItems) };
      });
    },
    [calculateTotals]
  );

  const addAddon = useCallback(
    (addon: Addon, quantity: number, variant?: { name: string; priceModifier: number }) => {
      const effectivePrice = addon.price + (variant?.priceModifier ?? 0);
      addItem({
        type: 'addon',
        itemId: addon.id,
        name: addon.name,
        price: effectivePrice,
        quantity,
        variant: variant ? { name: variant.name, data: variant } : undefined,
      });

      setWizardState((prev) => {
        const existingIndex = prev.addons.selections.findIndex(
          (s) => s.addonId === addon.id && variantDataEqual(s.variantData, variant)
        );

        if (existingIndex >= 0) {
          const newSelections = [...prev.addons.selections];
          newSelections[existingIndex] = {
            ...newSelections[existingIndex],
            quantity: newSelections[existingIndex].quantity + quantity,
          };
          return { ...prev, addons: { selections: newSelections } };
        }

        return {
          ...prev,
          addons: {
            selections: [
              ...prev.addons.selections,
              { addonId: addon.id, quantity, variantData: variant },
            ],
          },
        };
      });
    },
    [addItem]
  );

  const removeAddon = useCallback(
    (addonId: string, variant?: { name: string }) => {
      setCart((prev) => {
        const newItems = prev.items.filter(
          (item) =>
            !(
              item.type === 'addon' &&
              item.itemId === addonId &&
              item.variant?.name === variant?.name
            )
        );
        return { items: newItems, ...calculateTotals(newItems) };
      });

      setWizardState((prev) => ({
        ...prev,
        addons: {
          selections: prev.addons.selections.filter(
            (s) => !(s.addonId === addonId && variantDataEqual(s.variantData, variant))
          ),
        },
      }));
    },
    [calculateTotals]
  );

  const addSpecialEvent = useCallback(
    (specialEvent: SpecialEvent, price?: number) => {
      const effectivePrice = price ?? specialEvent.ticketPrice ?? 0;
      addItem({
        type: 'special_event',
        itemId: specialEvent.id,
        name: specialEvent.title,
        price: effectivePrice,
        quantity: 1,
      });
    },
    [addItem]
  );

  const removeSpecialEvent = useCallback(
    (specialEventId: string) => {
      setCart((prev) => {
        const newItems = prev.items.filter(
          (item) => !(item.type === 'special_event' && item.itemId === specialEventId)
        );
        return { items: newItems, ...calculateTotals(newItems) };
      });
    },
    [calculateTotals]
  );

  // Wizard step management
  const setCurrentStep = useCallback((step: number) => {
    setWizardState((prev) => ({ ...prev, currentStep: step }));
  }, []);

  const completeStep = useCallback((step: number) => {
    setWizardState((prev) => ({
      ...prev,
      completedSteps: prev.completedSteps.includes(step)
        ? prev.completedSteps
        : [...prev.completedSteps, step],
    }));
  }, []);

  const isStepCompleted = useCallback(
    (step: number) => wizardState.completedSteps.includes(step),
    [wizardState.completedSteps]
  );

  // Computed values
  const itemsByType = useMemo(() => {
    const result: Record<CartItem['type'], CartItem[]> = {
      event_registration: [],
      tournament: [],
      activity: [],
      addon: [],
      special_event: [],
    };

    cart.items.forEach((item) => {
      result[item.type].push(item);
    });

    return result;
  }, [cart.items]);

  const hasEventRegistration = useMemo(
    () => cart.items.some((item) => item.type === 'event_registration'),
    [cart.items]
  );

  const tournamentIds = useMemo(
    () => cart.items.filter((item) => item.type === 'tournament').map((item) => item.itemId),
    [cart.items]
  );

  const activityIds = useMemo(
    () => cart.items.filter((item) => item.type === 'activity').map((item) => item.itemId),
    [cart.items]
  );

  const specialEventIds = useMemo(
    () => cart.items.filter((item) => item.type === 'special_event').map((item) => item.itemId),
    [cart.items]
  );

  const isEmpty = cart.items.length === 0;
  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return {
    // Cart state
    cart,
    wizardState,
    isHydrated,

    // Basic operations
    addItem,
    removeItem,
    updateQuantity,
    clearCart,

    // Type-specific operations
    addEventRegistration,
    addTournament,
    removeTournament,
    addActivity,
    removeActivity,
    addAddon,
    removeAddon,
    addSpecialEvent,
    removeSpecialEvent,

    // Wizard operations
    currentStep: wizardState.currentStep,
    setCurrentStep,
    completeStep,
    isStepCompleted,

    // Computed values
    itemsByType,
    hasEventRegistration,
    tournamentIds,
    activityIds,
    specialEventIds,
    isEmpty,
    itemCount,
    subtotal: cart.subtotal,
    tax: cart.tax,
    total: cart.total,
  };
}

// Context for sharing cart state across components
import { createContext, useContext, type ReactNode } from 'react';

interface CartContextValue extends ReturnType<typeof useCart> {
  flowConfig?: FlowConfig;
}

const CartContext = createContext<CartContextValue | null>(null);

interface CartProviderProps {
  children: ReactNode;
  flowConfig?: FlowConfig;
  taxRate?: number;
}

export function CartProvider({ children, flowConfig, taxRate }: CartProviderProps) {
  const effectiveTaxRate = flowConfig?.tax?.enabled ? flowConfig.tax.rate / 100 : (taxRate ?? 0);
  const cartState = useCart({ taxRate: effectiveTaxRate });

  return (
    <CartContext.Provider value={{ ...cartState, flowConfig }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCartContext() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCartContext must be used within a CartProvider');
  }
  return context;
}
